import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  MapPin, 
  Calendar, 
  GraduationCap, 
  Mail, 
  Github, 
  Linkedin, 
  Twitter, 
  User,
  Heart,
  Coffee,
  BookOpen,
  Target
} from 'lucide-react';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const socialLinks = [
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/in/dev-bhushan-bhatta-a9bb4133b/',
      icon: Linkedin,
      color: 'hover:text-blue-400'
    },
    {
      name: 'GitHub',
      url: 'https://github.com/Bhushan791',
      icon: Github,
      color: 'hover:text-gray-300'
    },
    {
      name: 'Twitter',
      url: 'https://x.com/Bhushan_Bhatta',
      icon: Twitter,
      color: 'hover:text-blue-400'
    },
    {
      name: 'Email',
      url: 'bhushanbhatta777@gmail.com',
      icon: Mail,
      color: 'hover:text-red-400'
    }
  ];

  const personalDetails = [
    {
      icon: MapPin,
      label: 'Location',
      value: 'Kathmandu, Nepal'
    },
    {
      icon: Calendar,
      label: 'Availability',
      value: 'Open to opportunities'
    },
    {
      icon: GraduationCap,
      label: 'Degree',
      value: 'BSc Hons in Software Engineering'
    }
  ];

  return (
    <section id="about" className="py-20 px-4 relative">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-primary-gold/3 to-primary-violet/3 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.2, 0.3, 0.2]
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <User className="w-8 h-8 text-primary-gold" />
            <h2 className="section-title gradient-text">About Me</h2>
            <Heart className="w-8 h-8 text-primary-gold" />
          </div>
          <p className="section-subtitle">Get to know me better</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Personal Story with Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="glass-effect p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-6">
                <BookOpen className="w-6 h-6 text-primary-gold" />
                <h3 className="text-2xl font-bold text-primary-text">My Story</h3>
              </div>
              
              <div className="flex flex-col items-center mb-8">
                {/* Enhanced Profile Photo */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="relative group mb-6"
                >
                  {/* Animated background rings */}
                  <div className="absolute -inset-6 rounded-full bg-gradient-to-r from-primary-gold/20 via-primary-violet/20 to-primary-gold/20 blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-500">
                    <motion.div
                      className="w-full h-full rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                    />
                  </div>
                  
                  {/* Secondary ring */}
                  <div className="absolute -inset-3 rounded-full bg-gradient-to-r from-primary-violet/30 to-primary-gold/30 blur-lg animate-pulse"></div>
                  
                  {/* Main image container */}
                  <div className="relative">
                    <img
                      src="/profile.png"
                      alt="Dev Bhushan Bhatta"
                      className="w-36 h-36 rounded-full object-cover border-4 border-primary-gold/40 shadow-2xl transform group-hover:scale-105 transition-all duration-500 relative z-10"
                      style={{
                        filter: 'brightness(1.1) contrast(1.05)',
                        boxShadow: '0 25px 50px rgba(0,0,0,0.3), inset 0 0 0 1px rgba(255,255,255,0.1)'
                      }}
                    />
                    
                    {/* Floating particles effect */}
                    <div className="absolute inset-0 rounded-full overflow-hidden pointer-events-none">
                      {[...Array(6)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute w-1.5 h-1.5 bg-primary-gold rounded-full opacity-70"
                          style={{
                            left: `${15 + (i * 12)}%`,
                            top: `${20 + (i * 10)}%`,
                          }}
                          animate={{
                            y: [-8, -16, -8],
                            x: [0, Math.sin(i) * 5, 0],
                            opacity: [0.7, 1, 0.7],
                            scale: [1, 1.3, 1]
                          }}
                          transition={{
                            duration: 2.5 + i * 0.3,
                            repeat: Infinity,
                            delay: i * 0.4
                          }}
                        />
                      ))}
                    </div>
                    
                    {/* Status indicator */}
                    <div className="absolute bottom-1 right-1 z-20">
                      <div className="relative">
                        <div className="w-5 h-5 bg-green-500 rounded-full border-2 border-white shadow-lg"></div>
                        <motion.div 
                          className="absolute inset-0 w-5 h-5 bg-green-500 rounded-full opacity-75"
                          animate={{ scale: [1, 1.4, 1], opacity: [0.75, 0, 0.75] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Hover tooltip */}
                  <motion.div 
                    className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    initial={{ y: 5 }}
                    whileHover={{ y: 0 }}
                  >
                    <div className="bg-primary-gold/90 text-black text-xs px-3 py-1.5 rounded-full whitespace-nowrap font-medium shadow-lg">
                      Available for work 🚀
                    </div>
                  </motion.div>
                </motion.div>
                
                {/* Name and title under image */}
                <div className="text-center">
                  <h4 className="text-xl font-bold text-primary-text mb-1">Dev Bhushan Bhatta</h4>
                  <p className="text-primary-muted text-sm">Software Engineering Student</p>
                </div>
              </div>
              
            <div className="space-y-4 text-primary-muted leading-relaxed">
  <p>
    I'm a passionate Software Engineering student from Nepal, driven by the blend of technology and creativity. I started programming out of curiosity, which grew into a love for building meaningful applications.
  </p>
  <p>
    Currently pursuing my BSc (Hons) in Software Engineering, I explore full-stack development, AI, and IoT projects that connect the digital and physical worlds.
  </p>
  <p>
    Outside coding, I enjoy teaching what I know and diving into music, which I absolutely love.
  </p>
</div>

            </div>

            {/* Personal Details */}
            <div className="glass-effect p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Target className="w-5 h-5 text-primary-gold" />
                <h4 className="text-lg font-semibold text-primary-text">Quick Info</h4>
              </div>
              <div className="space-y-3">
                {personalDetails.map((detail, index) => (
                  <motion.div
                    key={detail.label}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="p-2 bg-primary-gold/10 rounded-lg">
                      <detail.icon className="w-4 h-4 text-primary-gold" />
                    </div>
                    <div>
                      <span className="text-sm text-primary-muted">{detail.label}:</span>
                      <span className="ml-2 text-primary-text font-medium">{detail.value}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Social Links & Interests */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-6"
          >
            {/* Social Links */}
            <div className="glass-effect p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-6">
                <Coffee className="w-6 h-6 text-primary-gold" />
                <h3 className="text-2xl font-bold text-primary-text">Connect With Me</h3>
              </div>
              <p className="text-primary-muted mb-6">
                Let's connect and discuss technology, projects, or just have a chat about development!
              </p>
              <div className="grid grid-cols-2 gap-4">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                    whileHover={{ scale: 1.05, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    className={`flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-primary-gold/30 transition-all duration-300 group ${social.color}`}
                  >
                    <social.icon className="w-5 h-5 text-primary-gold group-hover:scale-110 transition-transform" />
                    <span className="font-medium text-primary-text">{social.name}</span>
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Interests & Hobbies */}
            <div className="glass-effect p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Heart className="w-5 h-5 text-primary-gold" />
                <h4 className="text-lg font-semibold text-primary-text">Interests</h4>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                    'Full-Stack Web Development',
  'AI & Machine Learning',
  'Internet of Things (IoT)',
  'Cloud Computing',
  'Data Science',
  'API Development',
  'Problem Solving',
  'Tech Exploration & Learning'
                ].map((interest, index) => (
                  <motion.div
                    key={interest}
                    initial={{ opacity: 0, y: 10 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.8 + index * 0.05 }}
                    className="flex items-center gap-2 p-2 rounded-lg bg-primary-gold/5 hover:bg-primary-gold/10 transition-colors"
                  >
                    <div className="w-2 h-2 bg-primary-gold rounded-full"></div>
                    <span className="text-sm text-primary-muted">{interest}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Current Focus */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1 }}
              className="glass-effect p-6 rounded-2xl"
            >
              <div className="flex items-center gap-3 mb-4">
                <Target className="w-5 h-5 text-primary-gold" />
                <h4 className="text-lg font-semibold text-primary-text">Current Focus</h4>
              </div>
              <p className="text-primary-muted text-sm leading-relaxed">
                Currently focused on mastering Backend development with Node.js, 
                exploring AI integration in web applications.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;